import { useEffect, useRef, useState, type FormEvent } from "react";
import { authConfigured, completeSignIn, currentUser, signIn, signOut } from "./auth";
import { createInvitation, loadAdminQueue, moderateInquiry, moderateOpportunity } from "./api";
import type { AdminUser, Inquiry, Invitation, OpportunitySubmission } from "./types";

function Brand() {
  return <span className="brand-word">ìncze<span>.</span></span>;
}

function dateTime(value: string): string {
  return new Intl.DateTimeFormat("de-AT", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

export function AuthCallbackPage() {
  const started = useRef(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    completeSignIn()
      .then(() => window.location.replace("/admin/"))
      .catch(() => setError(true));
  }, []);

  return (
    <main className="auth-callback">
      <Brand />
      <h1>{error ? "Anmeldung fehlgeschlagen" : "Anmeldung wird abgeschlossen…"}</h1>
      {error && <><p>Die Zitadel-Anmeldung konnte nicht bestätigt werden.</p><a className="button button-primary" href="/admin/">Erneut versuchen</a></>}
    </main>
  );
}

export function AdminPage() {
  const [phase, setPhase] = useState<"loading" | "signed-out" | "ready" | "forbidden" | "error">("loading");
  const [user, setUser] = useState<AdminUser>();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [invitationLink, setInvitationLink] = useState<string>();
  const [submissions, setSubmissions] = useState<OpportunitySubmission[]>([]);
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [busyId, setBusyId] = useState<string>();
  const [actionError, setActionError] = useState<string>();

  useEffect(() => {
    if (!authConfigured) {
      setPhase("error");
      return;
    }
    currentUser()
      .then((signedInUser) => {
        if (!signedInUser) {
          setPhase("signed-out");
          return undefined;
        }
        return loadAdminQueue().then((queue) => {
          setUser(queue.user);
          setInvitations(queue.invitations);
          setSubmissions(queue.submissions);
          setInquiries(queue.inquiries);
          setPhase("ready");
        });
      })
      .catch((error: unknown) => {
        setPhase(error instanceof Error && error.message.includes("403") ? "forbidden" : "error");
      });
  }, []);

  async function issueInvitation(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusyId("new-invitation");
    setActionError(undefined);
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    try {
      const created = await createInvitation({
        company: String(form.get("company")),
        email: String(form.get("email")),
        validDays: Number(form.get("validDays")),
      });
      const link = `${window.location.origin}/#invite=${encodeURIComponent(created.token)}`;
      setInvitationLink(link);
      setInvitations((items) => [created.item, ...items]);
      formElement.reset();
    } catch {
      setActionError("Die Einladung konnte nicht erstellt werden.");
    } finally {
      setBusyId(undefined);
    }
  }

  async function reviewSubmission(id: string, decision: "approved" | "rejected") {
    setBusyId(id);
    setActionError(undefined);
    try {
      const updated = await moderateOpportunity(id, decision);
      setSubmissions((items) => items.map((item) => item.id === id ? updated : item));
    } catch {
      setActionError("Die Entscheidung konnte nicht gespeichert werden.");
    } finally {
      setBusyId(undefined);
    }
  }

  async function reviewInquiry(id: string, decision: "handled" | "rejected") {
    setBusyId(id);
    setActionError(undefined);
    try {
      const updated = await moderateInquiry(id, decision);
      setInquiries((items) => items.map((item) => item.id === id ? updated : item));
    } catch {
      setActionError("Die Entscheidung konnte nicht gespeichert werden.");
    } finally {
      setBusyId(undefined);
    }
  }

  return (
    <div className="admin-shell">
      <header className="admin-header">
        <a href="/" aria-label="Zur Plattform"><Brand /></a>
        <div><span>Broker Administration</span>{phase === "ready" && <button type="button" onClick={() => void signOut()}>Abmelden</button>}</div>
      </header>

      {phase === "loading" && <main className="admin-state"><h1>Administration wird geladen…</h1></main>}
      {phase === "signed-out" && <main className="admin-state"><p className="eyebrow"><span />Geschützter Bereich</p><h1>Administration</h1><p>Melden Sie sich über Zitadel an, um Einträge und Anfragen zu moderieren.</p><button className="button button-primary" type="button" onClick={() => void signIn()}>Mit Zitadel anmelden <span>↗</span></button></main>}
      {phase === "forbidden" && <main className="admin-state"><h1>Kein Zugriff</h1><p>Ihr Konto hat nicht die erforderliche Rolle <code>broker-admin</code>.</p><button className="button button-primary" type="button" onClick={() => void signOut()}>Abmelden <span>↗</span></button></main>}
      {phase === "error" && <main className="admin-state"><h1>Administration nicht verfügbar</h1><p>Die Anmeldung oder die API-Konfiguration konnte nicht geladen werden.</p><a className="button button-primary" href="/admin/">Erneut versuchen <span>↻</span></a></main>}

      {phase === "ready" && <main className="admin-main">
        <div className="admin-title"><div><p className="eyebrow"><span />Moderation</p><h1>Broker Administration</h1></div><p>Angemeldet als <strong>{user?.name ?? user?.email ?? user?.sub}</strong></p></div>
        {actionError && <p className="admin-error" role="alert">{actionError}</p>}

        <section className="admin-section admin-invitations">
          <div className="admin-section-title"><h2>Einladungen</h2><span>Invite only</span></div>
          <form className="admin-invite-form" onSubmit={issueInvitation}>
            <label>Unternehmen<input name="company" required minLength={2} maxLength={120} /></label>
            <label>Geschäftliche E-Mail<input name="email" type="email" required maxLength={200} /></label>
            <label>Gültigkeit<select name="validDays" defaultValue="14"><option value="7">7 Tage</option><option value="14">14 Tage</option><option value="30">30 Tage</option></select></label>
            <button type="submit" disabled={busyId === "new-invitation"}>Einladung erstellen</button>
          </form>
          {invitationLink && <div className="admin-invite-link"><p>Dieser Link wird nur einmal angezeigt:</p><code>{invitationLink}</code><button type="button" onClick={() => void navigator.clipboard.writeText(invitationLink)}>Link kopieren</button></div>}
          <div className="admin-invite-list">
            {invitations.length === 0 && <p className="admin-empty">Noch keine Einladungen vorhanden.</p>}
            {invitations.map((item) => {
              const status = item.usedAt ? "verwendet" : item.expiresAt < new Date().toISOString() ? "abgelaufen" : "offen";
              return <article key={item.id}><div><strong>{item.company}</strong><a href={`mailto:${item.email}`}>{item.email}</a></div><span className={`admin-status admin-status--${item.usedAt ? "handled" : status === "offen" ? "pending" : "rejected"}`}>{status}</span><time>bis {dateTime(item.expiresAt)}</time></article>;
            })}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-title"><h2>Einträge</h2><span>{submissions.filter((item) => item.status === "pending").length} offen</span></div>
          <div className="admin-grid">
            {submissions.length === 0 && <p className="admin-empty">Keine Einreichungen vorhanden.</p>}
            {submissions.map((item) => <article className="admin-card" key={item.id}>
              <div className="admin-card-head"><span className={`admin-status admin-status--${item.status}`}>{item.status}</span><time>{dateTime(item.submittedAt)}</time></div>
              <p className="admin-kicker">{item.kind === "contract" ? "Auftrag" : "Kapazität"} · {item.city}, {item.country}</p>
              <h3>{item.title}</h3><p>{item.summary}</p>
              <dl><div><dt>Unternehmen</dt><dd>{item.company}</dd></div><div><dt>Branche</dt><dd>{item.sector}</dd></div><div><dt>Umfang</dt><dd>{item.volume}</dd></div><div><dt>Sprache</dt><dd>{item.locale.toUpperCase()}</dd></div></dl>
              <div className="admin-contact"><strong>{item.contactName}</strong><a href={`mailto:${item.contactEmail}`}>{item.contactEmail}</a>{item.website && <a href={item.website} target="_blank" rel="noreferrer">Website ↗</a>}</div>
              {item.status === "pending" && <div className="admin-actions"><button className="approve" disabled={busyId === item.id} onClick={() => void reviewSubmission(item.id, "approved")}>Veröffentlichen</button><button className="reject" disabled={busyId === item.id} onClick={() => void reviewSubmission(item.id, "rejected")}>Ablehnen</button></div>}
            </article>)}
          </div>
        </section>

        <section className="admin-section">
          <div className="admin-section-title"><h2>Kontaktanfragen</h2><span>{inquiries.filter((item) => item.status === "pending").length} offen</span></div>
          <div className="admin-grid">
            {inquiries.length === 0 && <p className="admin-empty">Keine Kontaktanfragen vorhanden.</p>}
            {inquiries.map((item) => <article className="admin-card" key={item.id}>
              <div className="admin-card-head"><span className={`admin-status admin-status--${item.status}`}>{item.status}</span><time>{dateTime(item.submittedAt)}</time></div>
              <p className="admin-kicker">Eintrag {item.opportunityId}</p><h3>{item.company}</h3><p>{item.message}</p>
              <div className="admin-contact"><strong>{item.name}</strong><a href={`mailto:${item.email}`}>{item.email}</a></div>
              {item.status === "pending" && <div className="admin-actions"><button className="approve" disabled={busyId === item.id} onClick={() => void reviewInquiry(item.id, "handled")}>Als bearbeitet markieren</button><button className="reject" disabled={busyId === item.id} onClick={() => void reviewInquiry(item.id, "rejected")}>Ablehnen</button></div>}
            </article>)}
          </div>
        </section>
      </main>}
    </div>
  );
}
